import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Tag, Plus, Pencil, Trash2, Users } from 'lucide-react';

const PALETTE = ['#3D4A8E', '#5C6BC0', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#0EA5E9', '#64748B'];

export default function CustomRoles() {
  const { user } = useAuth();
  const canManage = (user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: PALETTE[0], description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/custom-roles');
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/custom-roles/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/custom-roles', form);
        toast.success(`Custom role "${form.name}" created`);
      }
      setOpen(false); setEditing(null);
      setForm({ name: '', color: PALETTE[0], description: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, color: r.color || PALETTE[0], description: r.description || '' });
    setOpen(true);
  };
  const startCreate = () => {
    setEditing(null);
    setForm({ name: '', color: PALETTE[0], description: '' });
    setOpen(true);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? It will be removed from ${r.assigned_count} employee${r.assigned_count !== 1 ? 's' : ''}.`)) return;
    try {
      await api.delete(`/custom-roles/${r.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="custom-roles-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Tags</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Custom Roles</h1>
          <p className="text-slate-500 mt-1">
            {items.length} tag{items.length !== 1 ? 's' : ''} · Create custom labels like "Tech Lead", "Intern" or "Project Champion" and assign them to employees from their profile.
          </p>
          <p className="text-xs text-slate-400 mt-1">Note: these are display tags only. Permission roles (Admin / HR / Reporting Manager / Employee) are managed separately on each employee's profile.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-custom-role-button" onClick={startCreate} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Plus className="w-4 h-4 mr-2" /> New custom role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="custom-role-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editing ? 'Edit custom role' : 'New custom role'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Name *</Label>
                  <Input data-testid="custom-role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tech Lead, Intern, Project Champion" className="rounded-xl mt-1" autoFocus />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Colour</Label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="custom-role-colors">
                    {PALETTE.map(c => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                              data-testid={`custom-role-color-${c.replace('#','')}`}
                              className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-slate-900 scale-110' : 'border-slate-200'}`}
                              style={{ backgroundColor: c }} aria-label={c} />
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Description (optional)</Label>
                  <Textarea data-testid="custom-role-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl mt-1" />
                </div>
                <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Preview:</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: form.color }}>
                    <Tag className="w-3 h-3" /> {form.name || 'Tag preview'}
                  </span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-custom-role" onClick={save} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid="no-custom-roles">
          <Tag className="w-12 h-12 mx-auto opacity-30 mb-3" />
          {canManage ? 'No custom roles yet — create your first one to start tagging the team.' : 'No custom roles configured yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(r => (
            <div key={r.id} data-testid={`custom-role-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: r.color || '#3D4A8E' }}>
                    <Tag className="w-3 h-3" /> {r.name}
                  </span>
                  {r.description && <div className="text-sm text-slate-600 mt-2">{r.description}</div>}
                  <div className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> {r.assigned_count} employee{r.assigned_count !== 1 ? 's' : ''} tagged
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button data-testid={`edit-custom-role-${r.id}`} onClick={() => startEdit(r)} className="p-2 text-slate-400 hover:text-[#3D4A8E] rounded-lg hover:bg-slate-50">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-custom-role-${r.id}`} onClick={() => remove(r)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
